import { LinkTo } from '@ember/routing';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import {
  PrismCard,
  PrismLoadingBar,
  PrismMessage,
  PrismMetricBlock,
  PrismTooltip,
} from '@mylighthouse/prism-ember';
import dayjs from 'dayjs';
import { formatNumber, t } from 'ember-intl';
import { not } from 'ember-truth-helpers';
import { OTA_IDS } from 'frontend-utils/utils/constants/constant-values';
import HotelOverviewDashboardTilesMarketTrendsPriceDifferenceDailyViewCellRate from 'lumen/components/hotel/overview-dashboard/tiles/market-trends/price-difference-daily-view-cell-rate';
import isOdTileVisible from 'lumen/helpers/is-od-tile-visible';
import highlightElement from 'lumen/modifiers/highlight-element';
import {
  compsetDifference,
  compsetDifferenceBackground,
} from 'lumen/utils/compset-difference';
import { OD_SELECTED_TILES_IDS } from 'lumen/utils/constants/overview-dashboard-constants';
import findMedianRate from 'lumen/utils/numeric/median';
import { generateRatesTableRedirectUrlQueryParams } from 'lumen/utils/overview-dashboard/generate-rates-redirect-url-query-params';

interface HotelOverviewDashboardTilesMarketTrendsPriceDifferenceDailyViewSignature {
  Args: {
    compsetId: unknown;
    data: unknown;
    isLoading: unknown;
    otaId: unknown;
    rateParam: unknown;
    selectedRange: unknown;
    selectedRefDaysAgo: unknown;
    tileId: unknown;
  };
}

export default class HotelOverviewDashboardTilesMarketTrendsPriceDifferenceDailyView extends Component<HotelOverviewDashboardTilesMarketTrendsPriceDifferenceDailyViewSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare odSelections: Services['od-selections'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get compRatesArray() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return (
      this.hotelAndCompetitorsRateData
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        .filter((rateData) => !rateData.message && !rateData.isOwnHotel) // Filter out our own hotel and hotels where the rate has a message
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
        .map((rateData) => rateData.rates[0].value)
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get compRatesMedian() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    return findMedianRate(this.compRatesArray);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasOwnRate() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return this.ownRate && !this.ownRate.message;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hotelAndCompetitorsRateData() {
    // @ts-expect-error: Incorrect type
    let result = [];

    // Find out the competitors hotels that have no matching with the selected OTA.
    // The hotel with no matching is considered as a removed hotel and thus, should not be shown
    const filteredOwnHotelAndCompetitors =
      this.hotelAndMarketService.ownHotelAndCompetitors.filter(
        (hotelInfo) =>
          hotelInfo.otas.find((ota) => ota.id === this.args.otaId) !==
          undefined,
      );
    const availableHotelInfoIds = filteredOwnHotelAndCompetitors.map(
      (competitor) => competitor.id,
    );

    // Note: data can be null for this KPI (e.g. when a user selects a range only in the past, as this KPI only suppports forward looking data)
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (this.args.data && this.args.data.kpis.rates) {
      // Filter out the hotels that are removed i.e. have no booking matching (same behavior as in Rates tab)
      const filteredHotelInfoIds = Object.keys(
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
        this.args.data.kpis.rates,
      ).filter((hotelInfoId) => availableHotelInfoIds.includes(hotelInfoId));

      result = filteredHotelInfoIds
        .map((hotelInfoId) => {
          // Find hotel via hotelAndMarketService data
          const hotelDataInStore = filteredOwnHotelAndCompetitors.find(
            (hotel) => hotel.id === hotelInfoId,
          );

          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          const hotelRateObject = this.args.data.kpis.rates[hotelInfoId][0];

          let filteredHotelReferenceRateObject = null;

          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          const selectedRefDaysAgoInAPIFormat =
            // @ts-expect-error: Incorrect type
            this.odSelections.refDaysAgoDatesInAPIFormat[
              // @ts-expect-error: Incorrect type
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              this.args.selectedRefDaysAgo.id
            ];

          // Note: noticed that certain Rate objects didn't have comparison rates (e.g. when they were missing)
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          if (hotelRateObject.comparison_rates) {
            // We need to find the correct comparison rate by finding the rate with the extract date that matches the selected reference selection
            // extract_datetime example: 2022-02-14T06:16:10Z
            // First tried with string includes search, but this wasn't correct in all cases (due to timezones)

            // Note: added toLocalTime to be able to make a good comparison
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            filteredHotelReferenceRateObject =
              // @ts-expect-error: Incorrect type
              // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
              hotelRateObject.comparison_rates.find((comparisonRate) =>
                this.hotelAndMarketService
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                  .toLocalTime(selectedRefDaysAgoInAPIFormat)
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
                  .isSame(comparisonRate.extract_datetime, 'day'),
              );
          }

          // Hotels that have a soldout/missing rate will get pushed to the bottom of the list
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          let sortValue = hotelRateObject.value;
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          if (hotelRateObject.value === 0 || hotelRateObject.message) {
            sortValue = -Number.MAX_SAFE_INTEGER;
          }

          let hotelRateChange = null;
          let formattedHotelRateChange = null;

          if (
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            hotelRateObject.value !== 0 &&
            filteredHotelReferenceRateObject &&
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            filteredHotelReferenceRateObject?.value !== 0
          ) {
            hotelRateChange =
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              hotelRateObject.value - filteredHotelReferenceRateObject.value;

            if (hotelRateChange !== 0) {
              // Note: originally was formatted as percentage, but we decided to try out first by just showing the actual change (also default value that users see in Rates table with compare)
              // formattedHotelRateChange = this.intl.formatNumber(hotelRateChange / filteredHotelReferenceRateObject.value);
              formattedHotelRateChange = this.intl.formatNumber(
                hotelRateChange,
                {
                  maximumFractionDigits: 0,
                  minimumFractionDigits: 0,
                  signDisplay: 'always',
                },
              );
            }
          }

          return {
            // @ts-expect-error: Incorrect type
            name: hotelDataInStore.name,
            // @ts-expect-error: Incorrect type
            otas: hotelDataInStore.otas,
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            rates: this.args.data.kpis.rates[hotelDataInStore.id],
            isOwnHotel:
              // @ts-expect-error: Incorrect type
              this.hotelAndMarketService.isOwnHotelInfo(hotelDataInStore),
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            sortValue,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            message: hotelRateObject.message,
            // Note: this is actually an trimmed down rate object, but all necessary fields for formatting should be present
            formattedRate: this.hotelAndMarketService.formatRateObject(
              // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
              hotelRateObject,
              true,
              false,
            ),
            rateChangeVersusReference: hotelRateChange,
            formattedRateChangeVersusReference: formattedHotelRateChange,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            roomName: hotelRateObject.room_name,
          };
        })
        .sort((a, b) => {
          if (b.sortValue - a.sortValue !== 0) {
            return b.sortValue - a.sortValue;
          }

          // sort by message in case the sort values are same but messages are different
          if (a.message !== b.message) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
            return a.message.localeCompare(b.message);
          }

          // sort by hotel name in case both the sort values and messages are same
          return a.name.localeCompare(b.name);
        });
    }

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return result;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isAllChannelsSelected() {
    return this.args.otaId === OTA_IDS.ALL_CHANNELS;
  }

  // The redirect link redirects the user to Rates Insight table view
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get kpiRedirectUrlQueryParams() {
    let monthToRedirect = '';

    // @ts-expect-error: Incorrect type
    if (this.odSelections.isDayDrilldownActive) {
      monthToRedirect = dayjs(
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        this.odSelections.activeDrilldownDayAsString,
      ).format('YYYY-MM');
    } else {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      monthToRedirect = this.args.selectedRange.start.format('YYYY-MM');
    }

    return generateRatesTableRedirectUrlQueryParams(
      this.hotelAndMarketService,
      monthToRedirect,
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      this.odSelections.refDaysAgoDatesInAPIFormat[
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        this.args.selectedRefDaysAgo.id
      ],
      // @ts-expect-error: Incorrect type
      this.args.rateParam,
      this.args.otaId,
      this.args.compsetId,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get ownRate() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this.hotelAndCompetitorsRateData.find(
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      (rateData) => rateData.isOwnHotel,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    )?.rates[0];
  }

  // Price difference value text color should follow the same colors as in Rates chart
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get priceDifferenceTextColor() {
    const lowerLimit =
      this.hotelAndMarketService.hotel?.calendarColorLowerLimit;
    const upperLimit =
      this.hotelAndMarketService.hotel?.calendarColorUpperLimit;

    // @ts-expect-error: Incorrect type
    return compsetDifferenceBackground(
      this.priceDifferenceValue,
      lowerLimit,
      upperLimit,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get priceDifferenceValue() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
    return compsetDifference(this.ownRate.value, this.compRatesMedian);
  }

  <template>
    {{! @glint-nocheck }}
    <PrismCard
      @contentLayout="noPadding"
      class="prism-card--od-price-diff-daily-view
        {{if
          (not (isOdTileVisible OD_SELECTED_TILES_IDS.demand_level))
          'prism-card--od-price-diff-daily-view--without-demand-level'
        }}"
      {{highlightElement
        id=OD_SELECTED_TILES_IDS.pricing_difference
        isLoading=@isLoading
      }}
    >
      <:header as |Header|>
        <Header.title>
          {{t "overview_dashboard.kpi.price_difference"}}
        </Header.title>
      </:header>
      <:headerAfter>
        {{#if this.hotelAndCompetitorsRateData.length}}
          <LinkTo
            @model={{this.hotelAndMarketService.hotel.id}}
            @query={{this.kpiRedirectUrlQueryParams}}
            @route="hotel.rates"
            class="text-300--regular prism-link"
            data-event-id="hotel_overview-kpi_main_redirect-click"
            data-event-value={{@tileId}}
            target="_blank"
          >
            {{t "overview_dashboard.kpi.link.rate"}}
          </LinkTo>
        {{/if}}
      </:headerAfter>

      <:content>
        <div class="od-price-diff-daily-view-content">
          {{#if @isLoading}}
            <PrismLoadingBar />
          {{else}}
            {{#if this.hotelAndCompetitorsRateData.length}}
              <div class="od-price-diff-daily-view-compset">
                <PrismMetricBlock @align="left">
                  <:value>
                    <h3
                      class="text-200-bold price-difference-text-color
                        {{if this.hasOwnRate this.priceDifferenceTextColor}}"
                    >
                      {{#if this.hasOwnRate}}
                        {{formatNumber
                          this.priceDifferenceValue
                          signDisplay="always"
                          style="percent"
                        }}
                      {{else}}
                        {{t "general.missing"}}
                      {{/if}}
                    </h3>
                  </:value>
                  <:label>{{t "hotel.overview.vscompset"}}</:label>
                </PrismMetricBlock>
              </div>
              <div class="od-price-diff-daily-view-table-container">
                <table class="default-table od-price-diff-daily-view-table">
                  <thead class="table-header-fixed">
                    <tr>
                      <td class="text-200--bold col-hotel-name">
                        {{t "rates.hotelname"}}
                      </td>
                      <td class="text-200--bold col-rate">
                        {{! Don't show the change when the selected OTA is All Channels. }}
                        {{t "rates.rate"}}

                        {{#unless this.isAllChannelsSelected}}
                          ({{@selectedRefDaysAgo.label}})
                        {{/unless}}
                      </td>
                      <td class="text-200--bold">
                        {{t "general.room_name"}}
                      </td>
                    </tr>
                  </thead>
                  <tbody>
                    {{#each this.hotelAndCompetitorsRateData as |hotelData|}}
                      <tr class={{if hotelData.isOwnHotel "text-200--bold"}}>
                        <td class="col-hotel-name truncate">
                          {{hotelData.name}}

                          <PrismTooltip @side="top" @text={{hotelData.name}} />
                        </td>
                        <td class="col-rate">
                          <HotelOverviewDashboardTilesMarketTrendsPriceDifferenceDailyViewCellRate
                            @compsetId={{@compsetId}}
                            @hotel={{this.hotelAndMarketService.hotel}}
                            @hotelData={{hotelData}}
                            @isAllChannelsSelected={{this.isAllChannelsSelected}}
                            @otaId={{@otaId}}
                            @rateParam={{@rateParam}}
                            @selectedRange={{@selectedRange}}
                          />
                        </td>
                        <td class="truncate">
                          {{hotelData.roomName}}

                          {{#if hotelData.roomName}}
                            <PrismTooltip
                              @side="top"
                              @text={{hotelData.roomName}}
                            />
                          {{/if}}
                        </td>
                      </tr>
                    {{/each}}
                  </tbody>
                </table>
              </div>
            {{else}}
              <PrismMessage
                @illustration="100x100_critical"
                @title={{t "overview_dashboard.price_diff.title.past_date"}}
              />
            {{/if}}
          {{/if}}
        </div>
      </:content>
    </PrismCard>
  </template>
}
